// =============================================================================
// sales-settings.ts — Contrato Zod compartilhado da configuração comercial
// (`sales_settings` / `sales_outcome_codes`, F34-S02).
//
// Fonte única consumida pela API (validação de request/response,
// modules/sales/settings) e pelo frontend (F34-S06) — nenhum dos dois lados
// redeclara os limites de validação.
//
// LAZY DEFAULT (doc 26 §6.9, notas do slot F34-S02): uma organização que
// nunca salvou configuração comercial continua funcionando com os defaults
// exportados abaixo (`DEFAULT_CONTACT_WINDOW`, `DEFAULT_SALES_SETTINGS_INPUT`)
// — nada exige setup manual antes do primeiro uso. `distributorDryRun` nasce
// `true`: nenhuma organização começa distribuindo/disparando de verdade sem
// alguém desligar o dry-run explicitamente pela API.
//
// Convenção de dia da semana: `Date.prototype.getUTCDay()` — 0=domingo,
// 1=segunda, ..., 6=sábado (mesma convenção de apps/api/src/shared/dates.ts).
//
// LGPD: nada aqui é PII — fuso, janelas, tentativas e motivos de cancelamento/
// supressão são configuração operacional, não dado do titular.
// =============================================================================
import { z } from 'zod';
import { AssignmentStrategySchema, SALES_OUTCOME_CODES, SalesOutcomeCodeSchema } from './sales.js';
// ---------------------------------------------------------------------------
// Helpers de validação
// ---------------------------------------------------------------------------
/** HH:MM em 24h. Mesmo padrão espelhado no CHECK do banco (salesSettings.ts). */
const TIME_FORMAT_RE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const TimeStringSchema = z
    .string()
    .regex(TIME_FORMAT_RE, { message: 'Horário inválido — use o formato HH:MM (24h)' });
/**
 * Valida um nome de fuso IANA real usando o motor ICU do runtime (`Intl`) —
 * não existe validador estático confiável sem uma tabela IANA embutida, e
 * duplicar essa tabela aqui divergiria da versão do Node em produção.
 */
function isValidIanaTimeZone(value) {
    try {
        // Lança RangeError para fuso desconhecido — é o único jeito confiável de
        // validar contra a tabela IANA carregada pelo próprio runtime.
        Intl.DateTimeFormat(undefined, { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
export const IanaTimeZoneSchema = z.string().min(1).max(64).refine(isValidIanaTimeZone, {
    message: 'Fuso horário inválido — informe um identificador IANA válido (ex: America/Porto_Velho)',
});
// ---------------------------------------------------------------------------
// Janela de contato semanal
// ---------------------------------------------------------------------------
export const ContactWindowSchema = z
    .object({
    /** Dias permitidos, convenção `getUTCDay()` (0=domingo..6=sábado). Ao menos 1, sem duplicatas. */
    days: z
        .array(z.number().int().min(0).max(6))
        .min(1, { message: 'Informe ao menos um dia da semana permitido' })
        .max(7)
        .refine((days) => new Set(days).size === days.length, {
        message: 'Dias da semana não podem se repetir',
    }),
    startTime: TimeStringSchema,
    endTime: TimeStringSchema,
})
    .refine((window) => window.startTime < window.endTime, {
    message: 'O horário final deve ser depois do horário inicial',
    path: ['endTime'],
});
/**
 * Default conservador: segunda a sábado (exclui domingo), 08:00–20:00 — mesma
 * janela praticada pela regulação brasileira de telemarketing (Decreto
 * 11.034/2022: proibido aos domingos, restrito a 08h–20h nos demais dias).
 */
export const DEFAULT_CONTACT_WINDOW = {
    days: [1, 2, 3, 4, 5, 6],
    startTime: '08:00',
    endTime: '20:00',
};
// ---------------------------------------------------------------------------
// sales_settings — resposta (sempre com defaults aplicados)
// ---------------------------------------------------------------------------
export const SalesSettingsSchema = z.object({
    organizationId: z.string().uuid(),
    /**
     * Fuso IANA EFETIVO — o override desta configuração quando definido, ou o
     * `organizations.timezone` herdado caso contrário. Nunca vazio/nulo na
     * resposta, mesmo quando a organização nunca configurou nada.
     */
    timezone: z.string(),
    contactWindow: ContactWindowSchema,
    /** Tentativas máximas por lead antes de encerrar/pular. */
    maxAttempts: z.number().int().min(1).max(30),
    /** Intervalo mínimo (minutos) entre tentativas ao mesmo lead. */
    minIntervalMinutes: z.number().int().min(0).max(10080),
    /** Estratégias de distribuição habilitadas para uso em novas campanhas. */
    enabledAssignmentStrategies: z.array(AssignmentStrategySchema).min(1),
    cancellationReasons: z.array(z.string().min(1).max(200)).max(50),
    suppressionReasons: z.array(z.string().min(1).max(200)).max(50),
    /** Modo de simulação do distribuidor. `true` = nada é distribuído/disparado de verdade. */
    distributorDryRun: z.boolean(),
    /**
     * `true` quando a organização NUNCA salvou configuração comercial — os
     * valores acima são o default lazy, nenhuma linha existe em
     * `sales_settings`. Permite ao frontend (F34-S06) diferenciar "configurado
     * explicitamente com estes valores" de "ainda não configurado".
     */
    isDefault: z.boolean(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// PATCH /api/sales/settings
// ---------------------------------------------------------------------------
export const SalesSettingsPatchBodySchema = z
    .object({
    /** Override do fuso desta configuração. Omitir preserva o valor atual (ou continua herdando organizations.timezone). */
    timezone: IanaTimeZoneSchema.optional(),
    contactWindow: ContactWindowSchema.optional(),
    maxAttempts: z.number().int().min(1).max(30).optional(),
    minIntervalMinutes: z.number().int().min(0).max(10080).optional(),
    enabledAssignmentStrategies: z.array(AssignmentStrategySchema).min(1).optional(),
    cancellationReasons: z.array(z.string().min(1).max(200)).max(50).optional(),
    suppressionReasons: z.array(z.string().min(1).max(200)).max(50).optional(),
    distributorDryRun: z.boolean().optional(),
})
    .refine((body) => Object.keys(body).length > 0, {
    message: 'Informe ao menos um campo para atualizar',
});
// ---------------------------------------------------------------------------
// sales_outcome_codes — configuração por desfecho
// ---------------------------------------------------------------------------
export const SalesOutcomeConfigSchema = z.object({
    code: SalesOutcomeCodeSchema,
    enabled: z.boolean(),
    label: z.string().min(1).max(120),
    requiresNote: z.boolean(),
    requiresAppointment: z.boolean(),
    terminatesMember: z.boolean(),
    blocksContact: z.boolean(),
    sortOrder: z.number().int().min(0).max(1000),
});
// ---------------------------------------------------------------------------
// GET /api/sales/outcomes — resposta (só desfechos habilitados, ordenados)
// ---------------------------------------------------------------------------
export const SalesOutcomesResponseSchema = z.object({
    data: z.array(SalesOutcomeConfigSchema),
    /** `true` quando a organização nunca salvou configuração de desfechos — `data` reflete o catálogo canônico (F32-S01) inteiro, todos habilitados. */
    isDefault: z.boolean(),
});
// ---------------------------------------------------------------------------
// PUT /api/sales/outcomes — REPLACE completo do catálogo configurado
//
// Exige EXATAMENTE os códigos do catálogo canônico (`SALES_OUTCOME_CODES`,
// F32-S01) — nem a mais, nem a menos. `SalesOutcomeCodeSchema` já restringe
// cada item a um código do enum fechado (um código fora do catálogo é
// rejeitado pelo Zod antes de chegar a esta validação cruzada). Exigir o
// conjunto COMPLETO (em vez de aceitar um subconjunto) mantém a semântica de
// "teto fechado" simples: a organização decide `enabled` por código, mas
// nunca deixa um código do catálogo sem configuração — o service nunca
// precisa mesclar linhas parciais com defaults do catálogo.
// ---------------------------------------------------------------------------
export const SalesOutcomesPutBodySchema = z
    .object({
    data: z.array(SalesOutcomeConfigSchema),
})
    .superRefine((body, ctx) => {
    const codes = body.data.map((item) => item.code);
    const uniqueCodes = new Set(codes);
    if (uniqueCodes.size !== codes.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['data'],
            message: 'Há códigos de desfecho duplicados na lista',
        });
    }
    const missing = SALES_OUTCOME_CODES.filter((code) => !uniqueCodes.has(code));
    if (missing.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['data'],
            message: `Faltam desfechos do catálogo: ${missing.join(', ')}`,
        });
    }
});
//# sourceMappingURL=sales-settings.js.map